<template>
	<div class="view-content">
		<div class="page-heading">
			<h1>Funnel Performance</h1>
		</div>
		<query-form v-model:query="query" :loading="loading" @apply="getData(true)">
			<template #display>
				<div class="display-value">
					<div class="label">Date Range:</div>
					<div class="value">
						{{ formatDate(query.date_range, 'ddd, MMM DD, YYYY') }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Funnel ID:</div>
					<div class="value">
						{{ displaySelected(query.filters.site_id, site_id_options) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Vertical:</div>
					<div class="value">
						{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Placement:</div>
					<div class="value">
						{{ displaySelected(query.filters.placement, placement_options) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Ad Network:</div>
					<div class="value">
						{{ displaySelected(query.filters.ad_network, ad_network_options) }}
					</div>
				</div>
				<div class="display-value">
					<div class="label">Source ID:</div>
					<div class="value">
						{{ query.filters.source_id || 'None' }}
					</div>
				</div>
			</template>
			<template #utils>
				<p-button
					v-tooltip.top="'Refresh'"
					icon="pi pi-refresh"
					aria-label="Refresh"
					class="mr-2"
					@click="getData(true)"
				/>
			</template>
			<gutter size="20px" />
			<template #form="form">
				<row>
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Date Range:</label>
								<div class="controls">
									<div class="field">
										<date-range-picker :max-date_range="62" v-model="form.query.date_range" />
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Ad Network:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											v-model="form.query.filters.ad_network"
											:options="ad_network_options"
											option-label="label"
											option-value="value"
											display-selected-label
											show-clear
											filter
											placeholder="Select Ad Network"
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Funnel ID:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											v-model="form.query.filters.site_id"
											:options="site_id_options"
											placeholder="Select Funnel ID"
											option-label="label"
											option-value="value"
											display-selected-label
											show-clear
											filter
										/>
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter size="40px" />
					<column max-width="350px">
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Placement:</label>
								<div class="controls">
									<div class="field">
										<p-dropdown
											v-model="form.query.filters.placement"
											:options="placement_options"
											option-label="label"
											option-value="value"
											placeholder="Select Placement"
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Vertical:</label>
								<div class="controls">
									<div class="field">
										<p-multi-select
											v-model="form.query.filters.vertical_id"
											placeholder="Select Vertical"
											:options="$root.appStore.verticalOptions"
											option-label="label"
											option-value="value"
											display-selected-label
											show-clear
											filter
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="control-group">
							<div class="inner">
								<label class="control-label">Source ID:</label>
								<div class="controls">
									<div class="field">
										<p-input-text v-model="form.query.filters.source_id" placeholder="Source ID" />
									</div>
								</div>
							</div>
						</div>
					</column>
					<gutter class="hide-mobile" size="40px" />
					<column class="hide-mobile"></column>
				</row>
			</template>
		</query-form>
		<div class="metrics">
			<row>
				<div class="column">
					<metric label="Impressions" :value="metrics.impressions" />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Clicks" :value="metrics.clicks" />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Ad Revenue" :value="metrics.ad_revenue" is-currency />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Revenue/Impression" :value="metrics.rpi" is-currency />
				</div>
				<gutter size="20px" />
				<div class="column">
					<metric label="Revenue/Click" :value="metrics.rpc" is-currency />
				</div>
			</row>
		</div>
		<gutter size="20px" />
		<div class="card padded">
			<!-- Funnel performance -->
			<p-data-table ref="ad-funnel-performance" :loading="loading" :value="funnel_performance_results">
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<template #empty> <div class="dim">No Data</div> </template>
				<p-column header="Site ID" sortable field="site_id">
					<template #body="row">
						{{ row.data.site_id }}
					</template>
				</p-column>
				<p-column field="vertical_id" header="Vertical ID" class="column-align-center">
					<template #body="row">
						<vertical-icon :vertical-id="row.data.vertical_id" size="30px" />
					</template>
				</p-column>
				<p-column header="Ad Network" sortable field="network_id">
					<template #body="row">
						{{ row.data.network_id }}
					</template>
				</p-column>
				<p-column header="Impressions" sortable field="impressions" class="align-center">
					<template #body="row">
						{{ row.data.impressions }}
					</template>
				</p-column>
				<p-column header="Clicks" sortable field="clicks" class="align-center">
					<template #body="row">
						{{ row.data.clicks }}
					</template>
				</p-column>
				<p-column header="Expected Revenue" sortable field="expected_revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.expected_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Ad Revenue" sortable field="ad_revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.ad_revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Total Revenue" sortable field="revenue" class="align-center">
					<template #body="row">
						{{ currency(row.data.revenue, 0) }}
					</template>
				</p-column>
				<p-column header="Expected vs Total" sortable field="evt" class="align-center">
					<template #body="row">
						{{ currency(row.data.evt, 0) }}
						<div :class="['sub-value', trend(calculateDelta(row.data.expected_revenue, row.data.revenue))]">
							{{ percentage(calculateDelta(row.data.expected_revenue, row.data.revenue), 2) }}
						</div>
					</template>
				</p-column>
				<p-column sortable field="rpi" class="align-center">
					<template #header>
						<span v-tooltip.top="'Revenue/Impression'">RPI</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpi, 2) }}
					</template>
				</p-column>
				<p-column sortable field="rpc" class="align-center">
					<template #header>
						<span v-tooltip.top="'Revenue/Click'">RPC</span>
					</template>
					<template #body="row">
						{{ currency(row.data.rpc, 2) }}
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script lang="ts">
import { currency, formatDate, title, percentage, displaySelected } from '@/lib/Filters';
import {
	getAdAuctionFunnelsAsOptions,
	getAdAuctionNetworksAsOptions,
	getAdAuctionPlacementsAsOptions,
	reportAdAuctionFunnelPerformance,
} from '@GQL';
import { get } from 'lodash-es';
import verticalIcon from '@/components/elements/VerticalIcon.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import tableTools from '@/components/widgets/TableTools.vue';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import metric from '@/components/widgets/Metric.vue';
import dayjs from 'dayjs';
export default {
	name: 'funnel-performance',
	components: {
		dateRangePicker,
		queryForm,
		tableTools,
		verticalIcon,
		metric,
	},
	inject: ['mq'],
	data() {
		return {
			loading: false,
			query: {
				date_range: [dayjs().subtract(1, 'week').toDate(), dayjs().endOf('day').toDate()],
				filters: {
					site_id: [],
					vertical_id: [],
					network_id: [],
					placement_id: [],
					source_id: '',
				},
			},
			ad_network_options: [],
			placement_options: [],
			site_id_options: [],
			total_rows: 0,
			funnel_performance_results: [],
		};
	},
	methods: {
		get,
		currency,
		formatDate,
		title,
		percentage,
		displaySelected,
		async getData(refresh = false) {
			this.loading = true;
			try {
				const results = await reportAdAuctionFunnelPerformance(this.query, true);
				// Add Expected vs Total row
				this.funnel_performance_results = results.map((row) => {
					return {
						...row,
						evt: row.expected_revenue - row.total_revenue,
						rpi: row.ad_revenue / row.impressions,
						rpc: row.ad_revenue / row.clicks,
					};
				});
			} finally {
				this.loading = false;
			}
		},
		calculateDelta(baseline, value) {
			return baseline / value - 1;
		},
		trend(value) {
			if (Math.abs(value) > 0.05) {
				if (value > 0) return 'trending-up';
				if (value < 0) return 'trending-down';
			}
			return '';
		},
	},
	async mounted() {
		this.site_id_options = await getAdAuctionFunnelsAsOptions();
		this.ad_network_options = await getAdAuctionNetworksAsOptions();
		this.placement_options = await getAdAuctionPlacementsAsOptions();
		await this.getData();
	},
};
</script>

<style lang="less" scoped>
@import (reference) '@/styles/responsive';
.chart-container {
	display: flex;
	justify-content: center;
	align-items: center;
	max-width: 100%;
	height: 100%;
}
</style>
